import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { jobsApi, type JobDeliveryDto, type UpdateJobDeliveryRequest } from '@/lib/jobs';

interface Props {
  jobId: number;
  rowVersionBase64: string;
  delivery: JobDeliveryDto;
  onSaved: (next: JobDeliveryDto) => void;
}

export function JobDeliveryEditor({ jobId, rowVersionBase64, delivery, onSaved }: Props) {
  const [form, setForm] = useState<JobDeliveryDto>(delivery);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (req: UpdateJobDeliveryRequest) => jobsApi.updateDelivery(jobId, req),
    onSuccess: (saved) => {
      setError(null);
      onSaved(saved);
    },
    onError: (e: unknown) => {
      const status = (e as { status?: number })?.status;
      if (status === 409) setError('Job was changed by another user. Refresh and retry.');
      else setError('Failed to save delivery details. Please try again.');
    },
  });

  return (
    <section aria-label="Delivery details" className="space-y-3 p-4 border rounded">
      <h3 className="text-lg font-medium">Delivery</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="deliveryTo">Deliver to</Label>
          <Input
            id="deliveryTo"
            maxLength={200}
            value={form.deliveryTo ?? ''}
            onChange={(e) => setForm({ ...form, deliveryTo: e.target.value || null })}
          />
        </div>
        <div>
          <Label htmlFor="deliveryAttn">Attention</Label>
          <Input
            id="deliveryAttn"
            maxLength={200}
            value={form.deliveryAttn ?? ''}
            onChange={(e) => setForm({ ...form, deliveryAttn: e.target.value || null })}
          />
        </div>
        <div>
          <Label htmlFor="deliveryPhone">Phone</Label>
          <Input
            id="deliveryPhone"
            maxLength={50}
            value={form.deliveryPhone ?? ''}
            onChange={(e) => setForm({ ...form, deliveryPhone: e.target.value || null })}
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="deliverySpecialInstructions">Special instructions</Label>
          <textarea
            id="deliverySpecialInstructions"
            maxLength={2000}
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={form.deliverySpecialInstructions ?? ''}
            onChange={(e) =>
              setForm({ ...form, deliverySpecialInstructions: e.target.value || null })
            }
            aria-label="Special instructions"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            id="toBeDelivered"
            checked={form.toBeDelivered}
            onCheckedChange={(c) => setForm({ ...form, toBeDelivered: c === true })}
            aria-label="To be delivered"
          />
          <span>To be delivered</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            id="collectByCourier"
            checked={form.collectByCourier}
            onCheckedChange={(c) => setForm({ ...form, collectByCourier: c === true })}
            aria-label="Collect by courier"
          />
          <span>Collect by courier</span>
        </label>
      </div>

      {error && (
        <p className="text-red-600 text-sm" role="alert">
          {error}
        </p>
      )}

      <Button
        onClick={() => mutation.mutate({ ...form, rowVersionBase64 })}
        disabled={mutation.isPending}
      >
        {mutation.isPending ? 'Saving…' : 'Save delivery'}
      </Button>
    </section>
  );
}
